import makePartsAndShipmentsApiRequest from '../utils/partsAndShipmentsApi';
import { PartV3 as PartPayload, PartFile } from './types.d';
import { error } from '../legacy/flash-messenger';
import { PART_FILE_UPLOAD_TIMEOUT_MS } from './constants';

interface UploadPartFileResponse {
  file: PartFile;
}

interface PartFileUrlsResponse {
  files: PartFile[];
}

/**
 * Upload a single image file for a part. The backend accepts one file per
 * request (multipart field `file`) and is provider-only. Rejections (unsupported
 * type, size, count) surface as the thrown ApiError's message; the caller flashes it.
 */
export const uploadPartFile = async (
  workOrderId: number,
  partId: number,
  file: File,
  workOrderUserRole: string,
): Promise<PartFile> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await makePartsAndShipmentsApiRequest<UploadPartFileResponse>({
    method: 'POST',
    url: `/parts/v1/${workOrderId}/${partId}/files`,
    data: formData,
    timeout: PART_FILE_UPLOAD_TIMEOUT_MS,
    headers: {
      // Let axios set multipart/form-data with the correct boundary instead of
      // the instance default of application/json.
      'Content-Type': undefined,
      'X-User-Role': workOrderUserRole,
    },
  });
  return response.file;
};

/**
 * Delete a single part file. Provider-only and uploader-only server-side; a
 * non-uploader receives a 403 whose message the caller flashes. Returns 204.
 */
export const deletePartFile = async (
  workOrderId: number,
  partId: number,
  fileId: number,
  workOrderUserRole: string,
): Promise<void> => {
  await makePartsAndShipmentsApiRequest<void>({
    method: 'DELETE',
    url: `/parts/v1/${workOrderId}/${partId}/files/${fileId}`,
    headers: {
      'X-User-Role': workOrderUserRole,
    },
  });
};

/**
 * Fetch every file on a part with a freshly minted presigned download URL.
 * URLs are short-lived (15 min), so re-fetch before preview/download.
 */
export const getPartFileUrls = async (
  workOrderId: number,
  partId: number,
  workOrderUserRole: string,
): Promise<PartFile[]> => {
  const response = await makePartsAndShipmentsApiRequest<PartFileUrlsResponse>({
    method: 'GET',
    url: `/parts/v1/${workOrderId}/${partId}/files`,
    headers: {
      'X-User-Role': workOrderUserRole,
    },
  });
  return response.files ?? [];
};

interface PartsApiResponse {
  metadata: {
    total: number;
    per_page: number;
    pages: number;
    page: number;
  };
  results: PartPayload[];
}

export const createPartApi = async (
  workOrderId: number,
  workOrderUserRole: string,
  companyId: number,
  statusId: number,
  partData: PartPayload,
): Promise<PartPayload | undefined> => {
  const payload = {
    parts: [partData],
  };
  try {
    const response = await makePartsAndShipmentsApiRequest<PartsApiResponse>({
      method: 'POST',
      url: `/parts/v1/${workOrderId}`,
      data: JSON.stringify(payload),
      params: {
        status_id: statusId,
      },
      headers: {
        'X-User-Role': workOrderUserRole,
        'X-Company-Id': companyId,
      },
    });

    return response.results[0];
  } catch (err) {
    error((err as Error).message || 'Failed to create part, please try again later.');
    throw err;
  }
};

export const updatePartApi = async (
  workOrderId: number,
  partId: number,
  workOrderUserRole: string,
  companyId: number,
  statusId: number,
  partData: PartPayload,
): Promise<PartPayload | undefined> => {
  try {
    const response = await makePartsAndShipmentsApiRequest<PartsApiResponse>({
      method: 'PUT',
      url: `/parts/v1/${workOrderId}/${partId}/`,
      data: JSON.stringify(partData),
      params: {
        status_id: statusId,
      },
      headers: {
        'X-User-Role': workOrderUserRole,
        'X-Company-Id': companyId,
      },
    });

    return response.results?.find((part: PartPayload) => part.id === partId);
  } catch (err) {
    error((err as Error).message || 'Failed to update part, please try again later.');
    throw err;
  }
};

export const checkExternalIdUniqueness = async (
  externalId: string,
  companyId: number,
  workOrderUserRole: string,
): Promise<boolean> => {
  try {
    const response = await makePartsAndShipmentsApiRequest<{ exists: boolean }>({
      method: 'GET',
      url: `/parts/v1/exists/external_id/${externalId}`,
      headers: {
        'X-User-Role': workOrderUserRole,
        'X-Company-Id': companyId,
      },
    });
    return !response?.exists;
  } catch (err) {
    error((err as Error).message || 'Failed to validate External ID, please try again later.');
    throw err;
  }
};
